import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  PDFDownloadLink,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
  Image,
} from "@react-pdf/renderer";
import logo from "../images/logo.jpeg";

// PDF Styles (reuse from invoice)
const pdfStyles = StyleSheet.create({
  page: {
    padding: 10,
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: "#0891b2",
    paddingBottom: 20,
  },
  companyName: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#0891b2",
  },
  companyDetails: {
    fontSize: 10,
    color: "#666",
    marginTop: 5,
  },
  invoiceTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#0891b2",
    textAlign: "right",
  },
  subtitle: {
    fontSize: 10,
    color: "#666",
    textAlign: "right",
    marginTop: 5,
  },
  infoSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 30,
    backgroundColor: "#f8f9fa",
    padding: 15,
    borderRadius: 5,
  },
  infoBlock: {
    flex: 1,
  },
  infoLabel: {
    fontSize: 10,
    color: "#666",
    marginBottom: 3,
    textTransform: "uppercase",
  },
  infoValue: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#333",
  },
  customerSection: {
    marginBottom: 30,
    padding: 15,
    backgroundColor: "#f8f9fa",
    borderRadius: 5,
  },
  table: {
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#ddd",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#0891b2",
    color: "white",
    padding: 10,
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
    padding: 8,
  },
  tableRowAlt: {
    backgroundColor: "#f9f9f9",
  },
  col1: { width: "35%", paddingHorizontal: 5 },
  col2: { width: "10%", textAlign: "center", paddingHorizontal: 5 },
  col3: { width: "15%", textAlign: "right", paddingHorizontal: 5 },
  col4: { width: "15%", textAlign: "right", paddingHorizontal: 5 },
  col5: { width: "10%", textAlign: "center", paddingHorizontal: 5 },
  col6: { width: "15%", textAlign: "right", paddingHorizontal: 5 },
  totals: {
    marginTop: 20,
    alignItems: "flex-end",
  },
  totalRow: {
    flexDirection: "row",
    padding: 5,
    width: "50%",
  },
  totalLabel: {
    flex: 1,
    textAlign: "right",
    marginRight: 10,
    fontSize: 11,
  },
  totalValue: {
    width: 120,
    textAlign: "right",
    fontSize: 11,
  },
  grandTotal: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#0891b2",
    borderTopWidth: 2,
    borderTopColor: "#0891b2",
    paddingTop: 10,
    marginTop: 5,
  },
  notes: {
    marginTop: 30,
    padding: 15,
    backgroundColor: "#f8f9fa",
    borderRadius: 5,
  },
  notesTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 8,
    color: "#0891b2",
  },
  notesText: {
    fontSize: 10,
    lineHeight: 1.4,
    color: "#666",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: "center",
    color: "#666",
    fontSize: 9,
    borderTopWidth: 1,
    borderTopColor: "#ddd",
    paddingTop: 10,
  },
  compliance: {
    fontSize: 8,
    color: "#0891b2",
    marginTop: 5,
  },
  logo: {
    width: 30,
    height: 30,
    objectFit: "contain",
  },
});

// PDF Document Component for Purchase Invoice
const PurchaseOrderPDFDocument = ({ purchaseOrderData }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-AE", {
      style: "currency",
      currency: purchaseOrderData.currency || "AED",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        {/* Header */}
        <View style={pdfStyles.header}>
          <View>
            <Image src={logo} style={pdfStyles.logo} />
            <Text style={pdfStyles.companyName}>
              {purchaseOrderData.company?.name || "Diamond Quality Constructions"}
            </Text>
            <Text style={pdfStyles.companyDetails}>
              {purchaseOrderData.company?.address || ""}
            </Text>
            <Text style={pdfStyles.companyDetails}>
              TRN: {purchaseOrderData.company?.trn || ""}
            </Text>
          </View>
          <View>
            <Text style={pdfStyles.invoiceTitle}>PURCHASE INVOICE</Text>
            <Text style={pdfStyles.subtitle}>UAE Federal Tax Authority</Text>
          </View>
        </View>

        {/* PO Info */}
        <View style={pdfStyles.infoSection}>
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Invoice Number</Text>
            <Text style={pdfStyles.infoValue}>
              {purchaseOrderData.poNumber}
            </Text>
          </View>
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Invoice Date</Text>
            <Text style={pdfStyles.infoValue}>
              {formatDate(purchaseOrderData.poDate)}
            </Text>
          </View>
        </View>

        <View style={[pdfStyles.infoSection, { marginTop: 10 }]}>
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Payment Status</Text>
            <Text style={pdfStyles.infoValue}>{purchaseOrderData.status}</Text>
          </View>
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Mode of Payment</Text>
            <Text style={pdfStyles.infoValue}>{purchaseOrderData.mode || "N/A"}</Text>
          </View>
        </View>

        {/* Supplier Details */}
        <View style={pdfStyles.customerSection}>
          <Text style={pdfStyles.infoLabel}>Supplier:</Text>
          <Text
            style={[pdfStyles.infoValue, { marginTop: 5, marginBottom: 3 }]}
          >
            {purchaseOrderData.supplierName}
          </Text>
          <Text style={pdfStyles.companyDetails}>
            {purchaseOrderData.supplierAddress}
          </Text>
          {purchaseOrderData.supplierTrn && (
            <Text style={pdfStyles.companyDetails}>
              TRN: {purchaseOrderData.supplierTrn}
            </Text>
          )}
          <Text style={pdfStyles.companyDetails}>
            {purchaseOrderData.supplierCity}, {purchaseOrderData.supplierCountry}
          </Text>
          {purchaseOrderData.supplierPhone && (
            <Text style={pdfStyles.companyDetails}>
              Phone: {purchaseOrderData.supplierPhone}
            </Text>
          )}
        </View>

        {/* Items Table */}
        <View style={pdfStyles.table}>
          <View style={pdfStyles.tableHeader}>
            <Text style={pdfStyles.col1}>Description</Text>
            <Text style={pdfStyles.col2}>Qty</Text>
            <Text style={pdfStyles.col3}>Unit Price</Text>
            <Text style={pdfStyles.col4}>Discount</Text>
            <Text style={pdfStyles.col5}>VAT</Text>
            <Text style={pdfStyles.col6}>Total</Text>
          </View>

          {purchaseOrderData.items?.map((item, index) => {
            const subtotal = item.quantity * item.unit_price;
            const discountAmount = item.discount_amount || 0;
            const afterDiscount = subtotal - discountAmount;
            const vatAmount = afterDiscount * (item.vat_percent / 100);
            const total = afterDiscount + vatAmount;

            return (
              <View
                key={index}
                style={[
                  pdfStyles.tableRow,
                  index % 2 === 1 ? pdfStyles.tableRowAlt : {},
                ]}
              >
                <Text style={pdfStyles.col1}>{item.description}</Text>
                <Text style={pdfStyles.col2}>
                  {item.quantity} {item.unit}
                </Text>
                <Text style={pdfStyles.col3}>
                  {formatCurrency(item.unit_price)}
                </Text>
                <Text style={pdfStyles.col4}>
                  {formatCurrency(discountAmount)}
                </Text>
                <Text style={pdfStyles.col5}>{item.vat_percent}%</Text>
                <Text style={pdfStyles.col6}>{formatCurrency(total)}</Text>
              </View>
            );
          })}
        </View>

        {/* Totals Section */}
        <View style={pdfStyles.totals}>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Subtotal:</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(purchaseOrderData.totals?.subtotal || 0)}
            </Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Total Discount:</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(purchaseOrderData.totals?.totalDiscount || 0)}
            </Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Taxable Amount:</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(purchaseOrderData.totals?.taxableAmount || 0)}
            </Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>VAT Amount (5%):</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(purchaseOrderData.totals?.vatTotal || 0)}
            </Text>
          </View>
          <View style={[pdfStyles.totalRow, pdfStyles.grandTotal]}>
            <Text style={pdfStyles.totalLabel}>Total Amount:</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(purchaseOrderData.totals?.totalDue || 0)}
            </Text>
          </View>
        </View>

        {/* Notes */}
        {purchaseOrderData.notes && (
          <View style={pdfStyles.notes}>
            <Text style={pdfStyles.notesTitle}>Notes / Terms:</Text>
            <Text style={pdfStyles.notesText}>{purchaseOrderData.notes}</Text>
          </View>
        )}

        {/* Footer */}
        <View style={pdfStyles.footer}>
          <Text>
            This is a computer generated purchase invoice. No signature
            required.
          </Text>
          <Text style={pdfStyles.compliance}>
            🇦🇪 UAE VAT Compliant | Federal Tax Authority Approved | TRN:{" "}
            {purchaseOrderData.company?.trn || ""}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

const PurchaseOrderEntry = ({
  onSubmit = () => {},
  initialData,
  suppliers = [],
  companyInfo = null,
}) => {
  const [formData, setFormData] = useState(
    initialData || {
      poNumber: "",
      poDate: new Date().toISOString().split("T")[0],
      status: "unpaid",
      mode: "",
      currency: "AED",
      siteName: "",
      supplierId: "",
      customerId: "",

      // Supplier Information
      supplierName: "",
      supplierTrn: "",
      supplierPhone: "",
      supplierAddress: "",
      supplierCity: "",
      supplierCountry: "UAE",

      // Line Items
      items: [
        {
          description: "",
          quantity: 1,
          unit: "Units",
          unit_price: 0,
          discount_amount: 0,
          vat_percent: 5,
          total: 0,
        },
      ],

      // Notes
      notes: "",

      // Bank Details
      bankDetails: {
        iban: "AE07 0331 2345 6789 0123 456",
        bankName: "Emirates NBD",
        swift: "EBILAEA",
      },
    },
  );

  const [company] = useState(
    companyInfo || {
      name: "Diamond Quality Constructions",
      trn: "104836477000001",
      address: "Sharja, UAE",
      logo: "/logo.jpeg",
    },
  );

  const [items, setItems] = useState(formData.items);
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [errors, setErrors] = useState({});
  const [pdfData, setPdfData] = useState(null);
  const [shouldDownload, setShouldDownload] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadSession, setUploadSession] = useState({
    isActive: false,
    files: [],
    totalSize: 0,
  });
  const [suppliersData, setSuppliersData] = useState(suppliers || []);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [hasFetchedSuppliers, setHasFetchedSuppliers] = useState(false);
  const [customersData, setCustomersData] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [hasFetchedCustomers, setHasFetchedCustomers] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({
    state: "idle",
    message: "",
  });
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [popupMessage, setPopupMessage] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [itemOptions, setItemOptions] = useState([]); // Fixed: renamed from 'options' to 'itemOptions'
  const fileInputRef = React.useRef(null);
  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  // Reset form function
  const resetForm = () => {
    setFormData({
      poNumber: "",
      poDate: new Date().toISOString().split("T")[0],
      status: "unpaid",
      mode: "",
      currency: "AED",
      siteName: "",
      supplierId: "",
      customerId: "",
      supplierName: "",
      supplierTrn: "",
      supplierPhone: "",
      supplierAddress: "",
      supplierCity: "",
      supplierCountry: "UAE",
      items: [
        {
          description: "",
          quantity: 1,
          unit: "Units",
          unit_price: 0,
          discount_amount: 0,
          vat_percent: 5,
          total: 0,
        },
      ],
      notes: "",
      bankDetails: {
        iban: "AE07 0331 2345 6789 0123 456",
        bankName: "Emirates NBD",
        swift: "EBILAEA",
      },
    });
    setItems([
      {
        description: "",
        quantity: 1,
        unit: "Units",
        unit_price: 0,
        discount_amount: 0,
        vat_percent: 5,
        total: 0,
      },
    ]);
    setSelectedFile(null);
    setUploadStatus({ state: "idle", message: "" });
    setErrors({});
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Show popup message
  const showPopup = (type, title, message) => {
    setPopupMessage({ show: true, type, title, message });
    setTimeout(() => {
      setPopupMessage({ show: false, type: "", title: "", message: "" });
    }, 3000);
  };

  // Fetch suppliers
  useEffect(() => {
    if (hasFetchedSuppliers) return;
    if (suppliers && suppliers.length > 0) {
      setSuppliersData(suppliers);
      setSuppliersLoading(false);
      setHasFetchedSuppliers(true);
      return;
    }

    if (!apiUrl) {
      console.warn("API URL not configured, skipping supplier fetch");
      setSuppliersLoading(false);
      setHasFetchedSuppliers(true);
      return;
    }

    const fetchSuppliers = async () => {
      setSuppliersLoading(true);
      try {
        const response = await axios.get(`${apiUrl}/api/suppliers`);
        setSuppliersData(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching suppliers:", error);
        setSuppliersData([]);
      } finally {
        setSuppliersLoading(false);
        setHasFetchedSuppliers(true);
      }
    };

    fetchSuppliers();
  }, [suppliers, apiUrl, hasFetchedSuppliers]);

  // Fetch customers
  useEffect(() => {
    if (hasFetchedCustomers) return;
    if (!apiUrl) {
      console.warn("API URL not configured, skipping customer fetch");
      setCustomersLoading(false);
      setHasFetchedCustomers(true);
      return;
    }
    const fetchCustomers = async () => {
      setCustomersLoading(true);
      try {
        const response = await axios.get(`${apiUrl}/api/customers`);
        setCustomersData(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching customers:", error);
        setCustomersData([]);
      } finally {
        setCustomersLoading(false);
        setHasFetchedCustomers(true);
      }
    };

    fetchCustomers();
  }, [apiUrl, hasFetchedCustomers]);

  // FIXED: Fetch items for autocomplete
  useEffect(() => {
    const fetchItems = async () => {
      if (!apiUrl) {
        console.warn("API URL not configured, skipping items fetch");
        setItemOptions([]);
        return;
      }
      
      try {
        const response = await axios.get(`${apiUrl}/api/getpoitems`);
        console.log("Items API Response:", response.data);
        
        // Handle both response formats
        let itemsArray = [];
        if (response.data && Array.isArray(response.data)) {
          itemsArray = response.data;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          itemsArray = response.data.data;
        } else {
          itemsArray = [];
        }
        
        setItemOptions(itemsArray);
        console.log("Loaded item suggestions:", itemsArray.length);
      } catch (error) {
        console.error("Error fetching items:", error);
        setItemOptions([]);
      }
    };
    
    fetchItems();
  }, [apiUrl]);

  // UAE Emirates list
  const emirates = [
    "Abu Dhabi",
    "Dubai",
    "Sharjah",
    "Ajman",
    "Umm Al Quwain",
    "Ras Al Khaimah",
    "Fujairah",
  ];

  // Calculate line item total
  const calculateItemTotal = (item) => {
    const subtotal = (item.quantity || 0) * (item.unit_price || 0);
    const discountAmount = item.discount_amount || 0;
    const afterDiscount = subtotal - discountAmount;
    const vatAmount = afterDiscount * ((item.vat_percent || 0) / 100);
    return afterDiscount + vatAmount;
  };

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let totalDiscount = 0;
    let taxableAmount = 0;
    let vatTotal = 0;

    items.forEach((item) => {
      const itemSubtotal = (item.quantity || 0) * (item.unit_price || 0);
      const itemDiscount = item.discount_amount || 0;
      const itemAfterDiscount = itemSubtotal - itemDiscount;
      const itemVat = itemAfterDiscount * ((item.vat_percent || 0) / 100);

      subtotal += itemSubtotal;
      totalDiscount += itemDiscount;
      taxableAmount += itemAfterDiscount;
      vatTotal += itemVat;
    });

    const totalDue = taxableAmount + vatTotal;

    return {
      subtotal,
      totalDiscount,
      taxableAmount,
      vatTotal,
      totalDue,
    };
  };

  const { subtotal, totalDiscount, taxableAmount, vatTotal, totalDue } =
    calculateTotals();

  // Handle supplier selection
  const handleSupplierSelect = (supplierId) => {
    const supplier = suppliersData.find(
      (s) => s.id === parseInt(supplierId, 10),
    );
    if (supplier) {
      setFormData({
        ...formData,
        supplierId: parseInt(supplierId, 10),
        supplierName: supplier.name || "",
        supplierTrn: supplier.trn || "",
        supplierPhone: supplier.phone || "",
        supplierAddress: supplier.address || "",
        supplierCity: supplier.emirate || supplier.city || "",
        supplierCountry: supplier.country || "UAE",
      });
    }
  };

  // Add new item row
  const addItem = () => {
    setItems([
      ...items,
      {
        description: "",
        quantity: 1,
        unit: "Units",
        unit_price: 0,
        discount_amount: 0,
        vat_percent: 5,
        total: 0,
      },
    ]);
  };

  // Remove item row
  const removeItem = (index) => {
    if (items.length > 1) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
    }
  };

  // Update item field
  const updateItem = (index, field, value) => {
    const newItems = [...items];

    if (
      ["quantity", "unit_price", "discount_amount", "vat_percent"].includes(
        field,
      )
    ) {
      newItems[index][field] = parseFloat(value) || 0;
    } else {
      newItems[index][field] = value;
    }

    newItems[index].total = calculateItemTotal(newItems[index]);

    setItems(newItems);
  };

  // Update form field
  const updateField = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Handle single file upload
  const handleSingleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setUploadStatus({
          state: "error",
          message: "File size must be less than 5MB",
        });
        return;
      }
      setSelectedFile(file);
      setUploadStatus({ state: "idle", message: "" });
    }
  };

  // Clear file selection
  const clearFileSelection = () => {
    setSelectedFile(null);
    setUploadStatus({ state: "idle", message: "" });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .replace(/\//g, "/");
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.supplierName)
      newErrors.supplierName = "Supplier name is required";
    if (!formData.poNumber) newErrors.poNumber = "Invoice number is required";
    if (!formData.customerId) newErrors.customerId = "Site name is required";

    items.forEach((item, index) => {
      if (!item.description) {
        newErrors[`item_${index}_description`] = "Description required";
      }
      if (item.quantity <= 0) {
        newErrors[`item_${index}_quantity`] = "Quantity must be greater than 0";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit - show modal with options
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Transform items to match polines schema
      const transformedItems = items.map((item) => ({
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        unit_price: item.unit_price,
        vat_percent: item.vat_percent,
        discount_amount: item.discount_amount,
      }));

      // Prepare data for API according to database schema
      const apiData = {
        po_no: formData.poNumber,
        po_date: formData.poDate,
        status: formData.status === "unpaid" ? "Unpaid" : "Paid",
        notes: formData.notes,
        supplier_id: formData.supplierId,
        customer_id: formData.customerId || null,
        items: transformedItems,
      };

      const purchaseOrderData = {
        ...formData,
        items: items,
        company: company,
        totals: { subtotal, totalDiscount, taxableAmount, vatTotal, totalDue },
        uploadedFiles: uploadedFiles,
        formattedDates: {
          poDate: formatDateForDisplay(formData.poDate),
        },
      };

      const pdfPurchaseOrderData = {
        ...formData,
        items: transformedItems,
        company: company,
        totals: { subtotal, totalDiscount, taxableAmount, vatTotal, totalDue },
      };

      // Store data and show modal
      setPendingFormData({
        apiData,
        purchaseOrderData,
        pdfPurchaseOrderData,
      });
      setShowSaveModal(true);
    }
  };

  // Perform the actual save operation with double-click prevention
  const performSave = async (includeDownload = false) => {
    if (!pendingFormData || isSaving) return;

    setIsSaving(true);
    setShowSaveModal(false);

    const { apiData, purchaseOrderData, pdfPurchaseOrderData } =
      pendingFormData;

    if (apiUrl) {
      try {
        const formDataToSend = new FormData();
        formDataToSend.append("po_data", JSON.stringify(apiData));

        if (selectedFile) {
          formDataToSend.append("attachment", selectedFile);
        }

        const saveResponse = await axios.post(
          `${apiUrl}/api/pos`,
          formDataToSend,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        );

        showPopup(
          "success",
          "Success!",
          `Purchase invoice ${formData.poNumber} has been saved successfully.${includeDownload ? " PDF will be downloaded automatically." : ""}`,
        );

        if (selectedFile) {
          setSelectedFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }

        if (includeDownload) {
          setPdfData(pdfPurchaseOrderData);
          setShouldDownload(true);
        }

        resetForm();

        if (onSubmit && onSubmit !== (() => {})) {
          onSubmit(purchaseOrderData);
        }

        setPendingFormData(null);
      } catch (error) {
        console.error("Error saving Purchase Invoice:", error);
        showPopup(
          "error",
          "Error!",
          error.response?.data?.message ||
            "Failed to save purchase invoice. Please try again.",
        );
      } finally {
        setIsSaving(false);
      }
    } else {
      showPopup(
        "error",
        "Configuration Error",
        "API URL not configured. Please check your environment settings.",
      );
      setIsSaving(false);
    }
  };

  // Effect to handle PDF download
  useEffect(() => {
    if (shouldDownload && pdfData) {
      const generateAndDownloadPDF = async () => {
        try {
          const pdfDocument = (
            <PurchaseOrderPDFDocument purchaseOrderData={pdfData} />
          );
          const blob = await pdf(pdfDocument).toBlob();
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `PurchaseInvoice-${pdfData.poNumber || "New"}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          showPopup(
            "info",
            "PDF Generated",
            `PDF invoice ${pdfData.poNumber} has been downloaded successfully.`,
          );

          setShouldDownload(false);
          setPdfData(null);
        } catch (error) {
          console.error("Error generating PDF:", error);
          showPopup(
            "error",
            "PDF Error",
            "Error generating PDF. Please try again.",
          );
          setShouldDownload(false);
          setPdfData(null);
        }
      };

      generateAndDownloadPDF();
    }
  }, [shouldDownload, pdfData]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header with animated gradient */}
        <div className="bg-[#23A19B] rounded-t-2xl p-4 sm:p-8 text-white shadow-xl">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">
                Purchase Invoice Entry
              </h1>
              <p className="text-xs sm:text-base text-white/80">
                UAE Tax Compliant | FTA Approved Format
              </p>
            </div>
            <div className="badge badge-lg bg-white/20 text-white border-0 p-2 sm:p-4 shrink-0">
              <span className="text-lg sm:text-2xl font-bold">🇦🇪</span>
            </div>
          </div>
        </div>

        {/* Popup Notification */}
        {popupMessage.show && (
          <div
            className={`fixed top-20 right-4 z-50 animate-slide-in-right max-w-md w-full shadow-2xl rounded-lg ${
              popupMessage.type === "success"
                ? "bg-green-100 border-l-4 border-green-500"
                : popupMessage.type === "error"
                  ? "bg-red-100 border-l-4 border-red-500"
                  : "bg-blue-100 border-l-4 border-blue-500"
            }`}
          >
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {popupMessage.type === "success" && (
                    <svg
                      className="h-6 w-6 text-green-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                  {popupMessage.type === "error" && (
                    <svg
                      className="h-6 w-6 text-red-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  )}
                  {popupMessage.type === "info" && (
                    <svg
                      className="h-6 w-6 text-blue-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 9 9 0 0118 0z"
                      />
                    </svg>
                  )}
                </div>
                <div className="ml-3 flex-1">
                  <h3
                    className={`text-sm font-semibold ${
                      popupMessage.type === "success"
                        ? "text-green-800"
                        : popupMessage.type === "error"
                          ? "text-red-800"
                          : "text-blue-800"
                    }`}
                  >
                    {popupMessage.title}
                  </h3>
                  <p
                    className={`text-sm mt-1 ${
                      popupMessage.type === "success"
                        ? "text-green-700"
                        : popupMessage.type === "error"
                          ? "text-red-700"
                          : "text-blue-700"
                    }`}
                  >
                    {popupMessage.message}
                  </p>
                </div>
                <button
                  onClick={() =>
                    setPopupMessage({
                      show: false,
                      type: "",
                      title: "",
                      message: "",
                    })
                  }
                  className="ml-auto flex-shrink-0"
                >
                  <svg
                    className="h-5 w-5 text-gray-400 hover:text-gray-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Form Card */}
        <div className="bg-base-100 rounded-b-2xl shadow-2xl p-4 sm:p-8 mb-6 sm:mb-8 border-t-4 border-primary">
          <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
            {/* PO Details Section */}
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-4 sm:p-6 rounded-xl">
              <h3 className="text-lg sm:text-xl font-bold text-primary mb-3 sm:mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-primary rounded-full"></span>
                Purchase Invoice Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-primary">📄</span> Invoice NO.
                    </span>
                  </label>
                  <input
                    type="text"
                    className={`input input-bordered border-2 focus:border-primary ${errors.poNumber ? "input-error" : ""}`}
                    value={formData.poNumber}
                    onChange={(e) => updateField("poNumber", e.target.value)}
                    placeholder="PI-202603-001"
                  />
                  {errors.poNumber && (
                    <label className="label">
                      <span className="label-text-alt text-error flex items-center gap-1">
                        <span>⚠️</span> {errors.poNumber}
                      </span>
                    </label>
                  )}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-accent">📅</span> INVOICE DATE
                    </span>
                  </label>
                  <input
                    type="date"
                    className="input input-bordered border-2 focus:border-accent"
                    value={formData.poDate}
                    onChange={(e) => updateField("poDate", e.target.value)}
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-info">🏗️</span> SITE NAME{" "}
                      <span className="text-error">*</span>
                    </span>
                  </label>
                  <select
                    className={`select select-bordered border-2 focus:border-info ${errors.customerId ? "select-error" : ""}`}
                    value={formData.customerId}
                    onChange={(e) => {
                      const customer = customersData.find(
                        (c) => c.id === parseInt(e.target.value),
                      );
                      setFormData({
                        ...formData,
                        customerId: parseInt(e.target.value),
                        siteName: customer?.name || "",
                      });
                      if (errors.customerId) {
                        setErrors({ ...errors, customerId: null });
                      }
                    }}
                    disabled={customersLoading}
                  >
                    <option value="">Select Site</option>
                    {customersData.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                      </option>
                    ))}
                  </select>
                  {errors.customerId && (
                    <label className="label">
                      <span className="label-text-alt text-error flex items-center gap-1">
                        <span>⚠️</span> {errors.customerId}
                      </span>
                    </label>
                  )}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-secondary">💳</span> PAYMENT STATUS
                    </span>
                  </label>
                  <select
                    className="select select-bordered border-2"
                    value={formData.status}
                    onChange={(e) => updateField("status", e.target.value)}
                  >
                    <option value="paid">Paid</option>
                    <option value="unpaid">Unpaid</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Supplier Section */}
            <div className="bg-gradient-to-r from-secondary/10 to-accent/10 p-6 rounded-xl">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-secondary flex items-center gap-2">
                  <span className="w-1 h-6 bg-secondary rounded-full"></span>
                  Supplier Details
                </h3>
                <button
                  type="button"
                  className="btn btn-sm btn-outline btn-secondary"
                  onClick={() => setShowSupplierForm(!showSupplierForm)}
                >
                  {showSupplierForm ? "📋 Select Supplier" : "➕ Add New"}
                </button>
              </div>

              {!showSupplierForm ? (
                <div className="form-control mb-4">
                  {suppliersLoading ? (
                    <div className="flex items-center gap-2">
                      <span className="loading loading-spinner loading-sm"></span>
                      Loading suppliers...
                    </div>
                  ) : suppliersData.length > 0 ? (
                    <select
                      className="select select-bordered border-2"
                      onChange={(e) => handleSupplierSelect(e.target.value)}
                      defaultValue=""
                    >
                      <option value="" disabled>
                        Select a supplier
                      </option>
                      {suppliersData.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name} - {supplier.trn || "No TRN"}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="text-sm text-gray-500">
                      No suppliers available. Add a new supplier below.
                    </div>
                  )}
                </div>
              ) : null}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      SUPPLIER NAME *
                    </span>
                  </label>
                  <input
                    type="text"
                    className={`input input-bordered border-2 ${errors.supplierName ? "input-error" : ""}`}
                    value={formData.supplierName}
                    readOnly={!showSupplierForm}
                    onChange={(e) =>
                      updateField("supplierName", e.target.value)
                    }
                    placeholder=""
                  />
                  {errors.supplierName && (
                    <label className="label">
                      <span className="label-text-alt text-error flex items-center gap-1">
                        <span>⚠️</span> {errors.supplierName}
                      </span>
                    </label>
                  )}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      TRN (TAX REG. NO.)
                    </span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2 font-mono"
                    value={formData.supplierTrn}
                    readOnly={!showSupplierForm}
                    onChange={(e) => updateField("supplierTrn", e.target.value)}
                    placeholder="15-digit TRN"
                    maxLength="15"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">PHONE</span>
                  </label>
                  <input
                    type="tel"
                    className="input input-bordered border-2"
                    value={formData.supplierPhone}
                    readOnly={!showSupplierForm}
                    onChange={(e) =>
                      updateField("supplierPhone", e.target.value)
                    }
                    placeholder=""
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">ADDRESS</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2"
                    value={formData.supplierAddress}
                    readOnly={!showSupplierForm}
                    onChange={(e) =>
                      updateField("supplierAddress", e.target.value)
                    }
                    placeholder=""
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      CITY / EMIRATE
                    </span>
                  </label>
                  <select
                    className="select select-bordered border-2"
                    value={formData.supplierCity}
                    disabled={!showSupplierForm}
                    onChange={(e) =>
                      updateField("supplierCity", e.target.value)
                    }
                  >
                    <option value="">Select...</option>
                    {emirates.map((emirate) => (
                      <option key={emirate} value={emirate}>
                        {emirate}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">COUNTRY</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2"
                    value={formData.supplierCountry}
                    readOnly={!showSupplierForm}
                    onChange={(e) =>
                      updateField("supplierCountry", e.target.value)
                    }
                    placeholder="UAE"
                  />
                </div>
              </div>
            </div>

            {/* Line Items Section */}
            <div className="bg-gradient-to-r from-accent/10 to-primary/10 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-accent mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-accent rounded-full"></span>
                Line Items
              </h3>

              <div className="overflow-x-auto">
                <table className="table table-zebra w-full">
                  <thead className="bg-gradient-to-r from-primary to-secondary text-white">
                    <tr>
                      <th className="rounded-tl-lg">#</th>
                      <th>DESCRIPTION</th>
                      <th>QTY</th>
                      <th>UNIT</th>
                      <th>UNIT PRICE</th>
                      <th>DISC</th>
                      <th>VAT %</th>
                      <th className="rounded-tr-lg">TOTAL</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      const itemTotal = calculateItemTotal(item);

                      return (
                        <tr
                          key={index}
                          className="hover:bg-base-200 transition-colors"
                        >
                          <td className="font-bold text-primary">
                            {index + 1}
                          </td>
                          <td>
                            <input
                              list="description-suggestions"
                              className="input input-bordered input-sm w-40"
                              value={item.description}
                              onChange={(e) =>
                                updateItem(index, "description", e.target.value)
                              }
                              placeholder="Description"
                            />
                            <datalist id="description-suggestions">
                              {itemOptions.map((itemOption, i) => (
                                <option key={i} value={itemOption} />
                              ))}
                            </datalist>
                            {errors[`item_${index}_description`] && (
                              <div className="text-error text-xs mt-1">
                                {errors[`item_${index}_description`]}
                              </div>
                            )}
                          </td>
                          <td>
                            <input
                              type="number"
                              className="input input-bordered input-sm w-16"
                              value={item.quantity}
                              onChange={(e) =>
                                updateItem(index, "quantity", e.target.value)
                              }
                              min="1"
                              step="0.01"
                            />
                            {errors[`item_${index}_quantity`] && (
                              <div className="text-error text-xs mt-1">
                                {errors[`item_${index}_quantity`]}
                              </div>
                            )}
                          </td>
                          <td>
                            <select
                              className="select select-bordered select-sm w-20"
                              value={item.unit}
                              onChange={(e) =>
                                updateItem(index, "unit", e.target.value)
                              }
                            >
                              <option>Units</option>
                              <option>Hour</option>
                              <option>Day</option>
                              <option>Service</option>
                              <option>Piece</option>
                            </select>
                          </td>
                          <td>
                            <input
                              type="number"
                              className="input input-bordered input-sm w-20"
                              value={item.unit_price}
                              onChange={(e) =>
                                updateItem(index, "unit_price", e.target.value)
                              }
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td>
                            <div className="relative">
                              <input
                                type="number"
                                className="input input-bordered input-sm w-16"
                                value={item.discount_amount}
                                onChange={(e) =>
                                  updateItem(
                                    index,
                                    "discount_amount",
                                    e.target.value,
                                  )
                                }
                                min="0"
                                step="0.01"
                              />
                            </div>
                          </td>
                          <td>
                            <select
                              className="select select-bordered select-sm w-16"
                              value={item.vat_percent}
                              onChange={(e) =>
                                updateItem(index, "vat_percent", e.target.value)
                              }
                            >
                              <option value="0">0%</option>
                              <option value="5">5%</option>
                            </select>
                          </td>
                          <td className="text-right font-mono font-bold text-success">
                            AED {itemTotal.toFixed(2)}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs text-error hover:bg-error/20"
                              onClick={() => removeItem(index)}
                              disabled={items.length === 1}
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <button
                type="button"
                className="btn btn-outline btn-accent btn-sm mt-4 hover:scale-105 transition-transform"
                onClick={addItem}
              >
                ➕ Add Line Item
              </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div className="stat bg-gradient-to-br from-info/20 to-info/5 rounded-xl">
                <div className="stat-figure text-info">
                  <span className="text-2xl">💰</span>
                </div>
                <div className="stat-title">Subtotal</div>
                <div className="stat-value text-info text-2xl">
                  AED {subtotal.toFixed(2)}
                </div>
              </div>

              <div className="stat bg-gradient-to-br from-warning/20 to-warning/5 rounded-xl">
                <div className="stat-figure text-warning">
                  <span className="text-2xl">🏷️</span>
                </div>
                <div className="stat-title">Discount</div>
                <div className="stat-value text-warning text-2xl">
                  AED {totalDiscount.toFixed(2)}
                </div>
              </div>

              <div className="stat bg-gradient-to-br from-secondary/20 to-secondary/5 rounded-xl">
                <div className="stat-figure text-secondary">
                  <span className="text-2xl">🧾</span>
                </div>
                <div className="stat-title">VAT (5%)</div>
                <div className="stat-value text-secondary text-2xl">
                  AED {vatTotal.toFixed(2)}
                </div>
              </div>

              <div className="stat bg-gradient-to-br from-success/20 to-success/5 rounded-xl">
                <div className="stat-figure text-success">
                  <span className="text-2xl">💵</span>
                </div>
                <div className="stat-title">Total Amount</div>
                <div className="stat-value text-success text-2xl">
                  AED {totalDue.toFixed(2)}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="grid grid-cols-1 sm:grid-cols-1 gap-4 sm:gap-6">
              <div className="bg-gradient-to-br from-info/10 to-info/5 p-4 sm:p-6 rounded-xl">
                <label className="label">
                  <span className="label-text font-bold text-info text-base sm:text-lg flex items-center gap-2">
                    <span className="w-1 h-5 sm:h-6 bg-info rounded-full"></span>
                    NOTES / TERMS
                  </span>
                </label>
                <textarea
                  className="textarea textarea-bordered textarea-sm sm:textarea-md h-24 sm:h-32 w-full border-2 focus:border-info"
                  value={formData.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  placeholder="Notes"
                ></textarea>
              </div>
            </div>

            {/* File Upload Section */}
            <div className="bg-gradient-to-br from-purple/10 to-purple/5 p-4 sm:p-6 rounded-xl border-2 border-dashed border-purple/30">
              <h3 className="text-lg sm:text-xl font-bold text-purple mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-purple rounded-full"></span>
                📎 Attachment (Optional)
              </h3>

              <div className="space-y-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      Select File
                    </span>
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="file-input file-input-bordered file-input-sm w-full"
                    onChange={handleSingleFileUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip"
                    disabled={uploadStatus.state === "uploading"}
                  />
                  <label className="label">
                    <span className="label-text-alt text-xs">
                      Max file size: 5MB (PDF, DOC, XLS, JPG, PNG, ZIP)
                    </span>
                  </label>
                </div>

                {selectedFile && (
                  <div className="bg-success/10 border-l-4 border-success p-3 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">✅</span>
                      <div>
                        <p className="font-semibold text-sm text-success">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-base-content/60">
                          {(selectedFile.size / 1024).toFixed(2)} KB • Will be
                          uploaded with PO
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm text-error"
                      onClick={clearFileSelection}
                    >
                      ✕
                    </button>
                  </div>
                )}

                {!selectedFile && (
                  <div className="alert alert-info alert-sm">
                    <span>
                      ℹ️ File attachment is optional. Select a file and it will
                      be uploaded when you save the purchase invoice.
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-4 sm:pt-6 border-t">
              <button
                type="button"
                className="btn btn-ghost btn-sm sm:btn-lg hover:bg-base-200 order-2 sm:order-1"
                onClick={() => window.history.back()}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-lg bg-gradient-to-r from-primary to-secondary border-0 hover:scale-105 transition-transform shadow-lg"
              >
                💾 Save Purchase Invoice
              </button>
            </div>
          </form>
        </div>

        {/* Hidden PDF Download Link */}
        {pdfData && (
          <div style={{ display: "none" }}>
            <PDFDownloadLink
              document={
                <PurchaseOrderPDFDocument purchaseOrderData={pdfData} />
              }
              fileName={`PurchaseOrder-${pdfData.poNumber || "New"}.pdf`}
            >
              {({ loading }) => !loading && "Download PDF"}
            </PDFDownloadLink>
          </div>
        )}

        {/* Save Modal with Double-Click Prevention */}
        {showSaveModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Save Purchase Invoice
                </h2>
                <p className="text-gray-600 mt-2">
                  Choose how you want to save this invoice:
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => performSave(false)}
                  disabled={isSaving}
                  className={`w-full btn btn-lg btn-outline border-2 transition-all ${
                    isSaving
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-primary hover:text-white"
                  }`}
                >
                  {isSaving ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="loading loading-spinner loading-sm"></span>
                      Saving...
                    </span>
                  ) : (
                    "💾 Save Only"
                  )}
                </button>
                <button
                  onClick={() => performSave(true)}
                  disabled={isSaving}
                  className={`w-full btn btn-lg btn-primary bg-gradient-to-r from-primary to-secondary border-0 transition-transform ${
                    isSaving
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:scale-105"
                  }`}
                >
                  {isSaving ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="loading loading-spinner loading-sm"></span>
                      Saving...
                    </span>
                  ) : (
                    "💾 Save & Print PDF"
                  )}
                </button>
              </div>

              <button
                onClick={() => {
                  if (!isSaving) {
                    setShowSaveModal(false);
                    setPendingFormData(null);
                  }
                }}
                disabled={isSaving}
                className="w-full btn btn-ghost"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-base-500 mt-4">
          <p>
            🇦🇪 UAE VAT Compliant | Status: {formData.status} | Mode:{" "}
            {formData.mode || "N/A"}
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PurchaseOrderEntry;